import React, { useState, useRef } from 'react';

interface FlightPackage {
  id: string;
  title: string;
  duration: string;
  price: string;
  description: string;
  image: string;
  features: string[];
}

interface BookingFormData {
  name: string;
  email: string;
  phone: string;
  preferredDate: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  general?: string;
}

const FlightBooking: React.FC = () => {
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [formData, setFormData] = useState<BookingFormData>({
    name: '',
    email: '',
    phone: '',
    preferredDate: '',
    message: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const formRef = useRef<HTMLDivElement>(null);

  const packages: FlightPackage[] = [
    {
      id: 'schnupperflug-15',
      title: 'Schnupperflug',
      duration: '15 Minuten',
      price: '40€',
      description: 'Erleben Sie die Faszination des Fliegens hautnah. Der perfekte Einstieg in die Welt der Luftfahrt.',
      image: '/images/Flugsportverein_Bilder_3.jpg',
      features: ['15 Minuten Flugzeit', 'Erfahrener Pilot', 'Einführung ins Cockpit']
    },
    {
      id: 'schnupperflug-30',
      title: 'Schnupperflug',
      duration: '30 Minuten',
      price: '75€',
      description: 'Genießen Sie einen ausgedehnten Rundflug über die wunderschöne Landschaft Thüringens.',
      image: '/images/Flugsportverein_Bilder_6.jpg',
      features: ['30 Minuten Flugzeit', 'Erfahrener Pilot', 'Ausführliche Einweisung']
    },
    {
      id: 'pilot-fuer-einen-tag',
      title: 'Pilot für einen Tag',
      duration: 'ca. 3 Stunden',
      price: '230€',
      description: 'Steigen Sie ins Cockpit und übernehmen Sie das Steuer! Ein unvergessliches Erlebnis.',
      image: '/images/Flugsportverein_Bilder_7.jpg',
      features: ['1 Stunde Theorie & Briefing', 'Einführung ins Flugzeug', 'Ca. 1 Stunde Flugzeit (selbst fliegen)', 'Nachbesprechung']
    }
  ];

  const handlePackageSelect = (packageId: string) => {
    setSelectedPackage(packageId);
    setShowBookingForm(true);
    setSubmitStatus('idle');
    
    // Smooth scroll to form
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !selectedPackage) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    const selectedPkg = packages.find(pkg => pkg.id === selectedPackage);
    const packageName = selectedPkg ? `${selectedPkg.title} - ${selectedPkg.duration}` : selectedPackage;
    const packagePrice = selectedPkg ? selectedPkg.price : '';

    // Build email message with only filled fields
    let emailMessage = `Buchungsanfrage für: ${packageName}${packagePrice ? ` (${packagePrice})` : ''}\n\n`;
    emailMessage += `Name: ${formData.name}\n`;
    emailMessage += `E-Mail: ${formData.email}\n`;
    
    if (formData.phone && formData.phone.trim()) {
      emailMessage += `Telefon: ${formData.phone}\n`;
    }
    
    if (formData.preferredDate && formData.preferredDate.trim()) {
      emailMessage += `Wunschtermin: ${formData.preferredDate}\n`;
    }
    
    if (formData.message && formData.message.trim()) {
      emailMessage += `\nNachricht:\n${formData.message}\n`;
    }

    try {
      const response = await fetch('/contact_form_smtp.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: `Buchungsanfrage: ${packageName}`,
          message: emailMessage
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', preferredDate: '', message: '' });
        setErrors({});
        setShowBookingForm(false);
        
        // Scroll to top of section to show success message
        setTimeout(() => {
          formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      } else {
        setSubmitStatus('error');
        setErrors({ general: result.message || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.' });
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrors({ general: 'Verbindungsfehler. Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="rundflug" className="py-16 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
            Rundflüge & Gastflüge
          </h2>
          <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
            Erleben Sie die Freiheit des Fliegens
          </p>
          <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
            Wählen Sie Ihr Flugerlebnis und buchen Sie noch heute Ihren unvergesslichen Rundflug
          </p>
          <p className="mt-2 text-sm text-gray-500 font-medium italic">
            Preise gültig für Buchungen ab dem 23.05.2026
          </p>
        </div>

        {/* Success Message */}
        {submitStatus === 'success' && (
          <div className="mb-8 rounded-md bg-green-50 p-4 max-w-3xl mx-auto">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  Vielen Dank für Ihre Buchungsanfrage! Wir werden uns so schnell wie möglich bei Ihnen melden, um Ihren Wunschtermin zu bestätigen.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Package Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className="relative overflow-hidden rounded-2xl shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl group cursor-pointer"
              onClick={() => handlePackageSelect(pkg.id)}
            >
              {/* Background Image */}
              <div className="relative h-96">
                <img
                  src={pkg.image}
                  alt={pkg.title}
                  className="w-full h-full object-cover"
                />
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent"></div>
                
                {/* Content */}
                <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
                  <h3 className="text-2xl font-bold mb-1">{pkg.title}</h3>
                  <p className="text-lg font-medium text-blue-300 mb-2">{pkg.duration}</p>
                  
                  {/* Price */}
                  <div className="mb-4">
                    <span className="text-4xl font-extrabold">{pkg.price}</span>
                  </div>
                  
                  {/* Description */}
                  <p className="text-sm text-gray-200 mb-4 line-clamp-2">
                    {pkg.description}
                  </p>
                  
                  {/* Features */}
                  <ul className="text-sm space-y-1 mb-4">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center">
                        <svg className="h-4 w-4 mr-2 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  {/* CTA Button */}
                  <button
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200 transform group-hover:scale-105"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePackageSelect(pkg.id);
                    }}
                  >
                    Jetzt Buchungsanfrage stellen
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Booking Form */}
        {showBookingForm && (
          <div ref={formRef} className="max-w-3xl mx-auto bg-white shadow-2xl rounded-2xl overflow-hidden border-2 border-blue-100">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
              <h3 className="text-2xl font-bold text-white">Buchungsanfrage</h3>
              <p className="text-blue-100 mt-1">
                {selectedPackage && packages.find(pkg => pkg.id === selectedPackage)?.title} - {selectedPackage && packages.find(pkg => pkg.id === selectedPackage)?.duration}
              </p>
            </div>
            
            <div className="p-6">
              {errors.general && (
                <div className="mb-6 rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-red-800">
                        {errors.general}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Package Selection */}
                <div>
                  <label htmlFor="package" className="block text-sm font-medium text-gray-700">
                    Gewähltes Paket *
                  </label>
                  <select
                    id="package"
                    name="package"
                    value={selectedPackage || ''}
                    onChange={(e) => setSelectedPackage(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-600 focus:ring-blue-600 sm:text-sm"
                    disabled={isSubmitting}
                  >
                    {packages.map((pkg) => (
                      <option key={pkg.id} value={pkg.id}>
                        {pkg.title} - {pkg.duration} ({pkg.price})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Vor- und Nachname *
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Max Mustermann"
                    className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                      errors.name 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                        : 'border-gray-300 focus:border-blue-600 focus:ring-blue-600'
                    }`}
                    style={{ fontSize: '16px' }}
                    disabled={isSubmitting}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="max.mustermann@beispiel.de"
                    className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                      errors.email 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                        : 'border-gray-300 focus:border-blue-600 focus:ring-blue-600'
                    }`}
                    disabled={isSubmitting}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                {/* Phone (optional) */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    Telefon (optional)
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+49 123 456789"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-600 focus:ring-blue-600 sm:text-sm"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Preferred Date (optional) */}
                <div>
                  <label htmlFor="preferredDate" className="block text-sm font-medium text-gray-700">
                    Wunschtermin (optional)
                  </label>
                  <input
                    type="text"
                    name="preferredDate"
                    id="preferredDate"
                    value={formData.preferredDate}
                    onChange={handleInputChange}
                    placeholder="z.B. Samstag, 15. Juni 2025, nachmittags"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-600 focus:ring-blue-600 sm:text-sm"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Message (optional) */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                    Nachricht (optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={3}
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Haben Sie besondere Wünsche oder Fragen?"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-600 focus:ring-blue-600 sm:text-sm"
                    disabled={isSubmitting}
                  ></textarea>
                </div>

                {/* Submit Button */}
                <div className="flex gap-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`flex-1 inline-flex justify-center items-center py-3 px-6 border border-transparent shadow-sm text-base font-medium rounded-md text-white ${
                      isSubmitting
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Wird gesendet...
                      </>
                    ) : (
                      'Buchungsanfrage senden'
                    )}
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      setShowBookingForm(false);
                      setFormData({ name: '', email: '', phone: '', preferredDate: '', message: '' });
                      setErrors({});
                    }}
                    disabled={isSubmitting}
                    className="px-6 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
                  >
                    Abbrechen
                  </button>
                </div>

                <p className="text-xs text-gray-500">
                  * Pflichtfelder. Ihre Daten werden vertraulich behandelt und nicht an Dritte weitergegeben.
                </p>
              </form>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default FlightBooking;

